import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, AlertTriangle, CheckCircle2, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SubsidieCheckForm } from "@/components/SubsidieCheckForm";
import { CHECK_DATE, ISDE, FRIESE_PLUS, GEMEENTEN } from "@/data/subsidie";

const ExtLink = ({ href, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="inline-flex items-center gap-1.5 text-[#2F3438] underline underline-offset-4 decoration-[#2F3438]/30 hover:decoration-[#2F3438] transition-colors">
    {children} <ExternalLink className="h-3.5 w-3.5 shrink-0" />
  </a>
);

export default function Subsidie() {
  return (
    <main data-testid="subsidie-page" className="bg-white">
      <Seo
        title="Subsidie kunststof kozijnen Friesland 2026 | DEKO"
        description="Benieuwd naar subsidie voor kunststof kozijnen en isolerend glas in Friesland? DEKO helpt u vrijblijvend inzicht te krijgen in landelijke, provinciale en gemeentelijke regelingen."
      />

      {/* HERO */}
      <section className="pt-40 pb-16 mx-auto max-w-[1440px] px-6 md:px-10">
        <Reveal>
          <div className="inline-flex items-center gap-2 bg-[#F7F7F5] border border-[#E4E4E1] rounded-sm px-4 py-2 text-sm text-[#2F3438]" data-testid="subsidie-datum-badge">
            <BadgeCheck className="h-4 w-4" /> Actueel gecontroleerd op {CHECK_DATE}
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#2F3438] mt-6 leading-tight max-w-4xl">
            Subsidie voor kunststof kozijnen en isolerend glas in Fryslân.
          </h1>
          <p className="text-[#222]/60 text-lg font-light leading-relaxed mt-6 max-w-2xl">
            Wie zijn woning isoleert met nieuw isolerend glas of kunststof kozijnen, kan mogelijk gebruikmaken van
            landelijke, provinciale en gemeentelijke regelingen. Op deze pagina vindt u uitsluitend informatie die wij
            hebben gecontroleerd bij de officiële bronnen: RVO, Provincie Fryslân en de gemeenten zelf. Bij iedere
            regeling staat een directe link naar de officiële bron, zodat u alles zelf kunt nakijken.
          </p>
        </Reveal>
      </section>

      {/* ISDE */}
      <section className="bg-[#F7F7F5] py-20 md:py-28" data-testid="isde-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">Landelijk — RVO</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#2F3438] mt-5">ISDE: landelijke subsidie voor isolerend glas.</h2>
            <p className="text-[#222]/60 font-light leading-relaxed mt-5 max-w-2xl">
              De Investeringssubsidie duurzame energie en energiebesparing (ISDE) is de landelijke regeling voor
              woningeigenaren. De onderstaande bedragen gelden voor maatregelen uitgevoerd vanaf 1 januari 2025 en zijn
              overgenomen van de officiële RVO-pagina (door RVO gecontroleerd op {ISDE.gecontroleerdRvo}; door ons
              geraadpleegd voor deze pagina, actueel gecontroleerd op {CHECK_DATE}).
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-10 overflow-x-auto bg-white border border-[#E4E4E1] rounded-sm">
              <table className="w-full text-left text-sm" data-testid="isde-table">
                <thead>
                  <tr className="border-b border-[#E4E4E1] text-xs uppercase tracking-widest text-[#222]/45">
                    <th className="px-5 py-4 font-medium">Maatregel</th>
                    <th className="px-5 py-4 font-medium whitespace-nowrap">Subsidie</th>
                    <th className="px-5 py-4 font-medium">Belangrijkste eis</th>
                  </tr>
                </thead>
                <tbody>
                  {ISDE.bedragen.map((r, i) => (
                    <tr key={i} className="border-b border-[#F0F0EE] last:border-0">
                      <td className="px-5 py-4 text-[#2F3438] font-medium">{r.maatregel}</td>
                      <td className="px-5 py-4 text-[#2F3438] whitespace-nowrap font-semibold">{r.bedrag}</td>
                      <td className="px-5 py-4 text-[#222]/60 font-light">{r.eis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display font-semibold text-xl text-[#2F3438]">Belangrijkste voorwaarden</h3>
                <ul className="mt-4 space-y-3">
                  {ISDE.voorwaarden.map((v, i) => (
                    <li key={i} className="flex gap-3 text-[#222]/65 font-light leading-relaxed text-sm">
                      <CheckCircle2 className="h-4 w-4 text-[#2F3438] mt-0.5 shrink-0" /> {v}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-[#E4E4E1] rounded-sm p-8 h-fit">
                <h3 className="font-display font-semibold text-xl text-[#2F3438]">Zelf controleren bij RVO</h3>
                <p className="text-[#222]/60 font-light text-sm mt-3 leading-relaxed">
                  Voorwaarden en bedragen kunnen wijzigen. Controleer daarom altijd de actuele regeling bij RVO.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <a href={ISDE.bron} target="_blank" rel="noopener noreferrer" data-testid="isde-rvo-button" className="group inline-flex items-center justify-between gap-3 bg-[#2F3438] text-white px-6 py-4 rounded-sm hover:bg-[#222] transition-colors duration-300 text-sm font-medium">
                    Bekijk actuele ISDE-voorwaarden bij RVO <ExternalLink className="h-4 w-4" />
                  </a>
                  <a href={ISDE.meldcodes} target="_blank" rel="noopener noreferrer" data-testid="isde-meldcodes-link" className="inline-flex items-center justify-between gap-3 border border-[#2F3438]/25 text-[#2F3438] px-6 py-4 rounded-sm hover:bg-[#2F3438]/5 transition-colors duration-300 text-sm font-medium">
                    Officiële meldcodelijst hoogrendementsglas <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
                <div className="text-xs text-[#222]/45 mt-4">Officiële RVO-bron →</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KOZIJN VS GLAS */}
      <section className="bg-white py-20 md:py-28" data-testid="kozijn-vs-glas-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">Goed om te weten</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#2F3438] mt-5 leading-tight">Subsidie op glas is niet hetzelfde als subsidie op het kozijn.</h2>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-7">
            <div className="space-y-5 text-[#222]/65 font-light leading-relaxed">
              <p>
                Subsidie voor isolerend glas betekent niet automatisch dat uw volledige kunststof kozijn wordt
                gesubsidieerd. De landelijke ISDE vergoedt een bedrag <strong className="font-medium text-[#2F3438]">per m² glas</strong> —
                gemeten inclusief kozijn — maar de kosten van het kozijn zelf zijn geen aparte subsidiepost.
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-[#2F3438] mt-1 shrink-0" /><span><strong className="font-medium text-[#2F3438]">Subsidie op glas:</strong> ISDE keert uit per m² HR++ of triple glas. Bij triple glas is kozijnvervanging een voorwaarde voor het hoge bedrag (€ 111 per m²).</span></li>
                <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-[#2F3438] mt-1 shrink-0" /><span><strong className="font-medium text-[#2F3438]">Subsidie op het kozijn:</strong> er bestaat landelijk geen apart subsidiebedrag voor het kozijn zelf; het kozijn telt mee via het glasoppervlak en de kozijnvervangingseis.</span></li>
                <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-[#2F3438] mt-1 shrink-0" /><span><strong className="font-medium text-[#2F3438]">Complete isolatiemaatregel:</strong> gemeentelijke regelingen werken soms met een percentage of vast bedrag over de totale isolatiemaatregel. Wat precies subsidiabel is, verschilt per gemeente en moet per regeling worden gecontroleerd.</span></li>
              </ul>
              <p>
                Wij schrijven daarom nooit "€ X subsidie op uw kunststof kozijnen" als de officiële regeling alleen een
                bedrag per m² glas vergoedt. DEKO rekent graag voor uw specifieke situatie uit wat er wél mogelijk is.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FRIESE PLUS */}
      <section className="bg-[#2F3438] text-white py-20 md:py-28" data-testid="friese-plus-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-white/40">Provinciaal — Provincie Fryslân</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">De Friese Plus: extra steun bij een lager inkomen.</h2>
            <p className="text-white/65 font-light leading-relaxed mt-5 max-w-2xl">
              De "Subsidieregeling Provinciale plus lokale aanpak isolatie" is bedoeld voor eigenaar-bewoners met een
              verhoogd risico op energiearmoede. De regeling is {FRIESE_PLUS.geldig}. Onderstaande punten komen
              rechtstreeks uit de officiële regeling en de officiële provinciepagina.
            </p>
          </Reveal>
          <Reveal delay={0.06}>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-5xl">
              {FRIESE_PLUS.punten.map((p, i) => (
                <li key={i} className="flex gap-3 text-white/75 font-light leading-relaxed text-sm border-t border-white/10 pt-4">
                  <CheckCircle2 className="h-4 w-4 text-white mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href={FRIESE_PLUS.bron} target="_blank" rel="noopener noreferrer" data-testid="friese-plus-bron" className="inline-flex items-center gap-3 bg-white text-[#2F3438] px-6 py-4 rounded-sm hover:bg-white/90 transition-colors text-sm font-medium">
                Officiële Provincie Fryslân-bron <ExternalLink className="h-4 w-4" />
              </a>
              <a href={FRIESE_PLUS.regelgeving} target="_blank" rel="noopener noreferrer" data-testid="friese-plus-regelgeving" className="inline-flex items-center gap-3 border border-white/30 text-white px-6 py-4 rounded-sm hover:bg-white/10 transition-colors text-sm font-medium">
                Officiële regeling op Overheid.nl <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NIET STAPELEN WAARSCHUWING */}
      <section className="bg-white pt-20 md:pt-28 pb-10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <div className="border-l-4 border-[#2F3438] bg-[#F7F7F5] p-8 sm:p-10 rounded-sm" data-testid="stapel-waarschuwing">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-[#2F3438] shrink-0 mt-1" />
                <div>
                  <h3 className="font-display font-bold text-xl sm:text-2xl text-[#2F3438]">Let op: subsidies zijn niet altijd stapelbaar.</h3>
                  <p className="text-[#222]/65 font-light leading-relaxed mt-3 max-w-3xl">
                    Gemeentelijke, provinciale en landelijke regelingen hebben ieder hun eigen voorwaarden, maximale
                    bedragen en regels voor subsidiabele kosten. Daarom kunt u subsidiebedragen niet zomaar bij elkaar
                    optellen. DEKO controleert graag welke regelingen in uw situatie mogelijk gecombineerd kunnen worden.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GEMEENTETABEL */}
      <section className="bg-white py-16 md:py-20" data-testid="gemeenten-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">Gemeentelijk — per gemeente controleren</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#2F3438] mt-5">Gemeentelijke regelingen in Fryslân.</h2>
            <p className="text-[#222]/60 font-light leading-relaxed mt-5 max-w-2xl">
              Iedere Friese gemeente heeft haar eigen regeling, voorwaarden en budget. Gemeentelijke budgetten kunnen
              tussentijds wijzigen of uitgeput raken. Daarom publiceren wij per gemeente geen bedrag zonder dat de
              actuele regeling op dat moment officieel is bevestigd — betrouwbaarheid gaat bij ons boven volledigheid.
              Via de officiële bron per gemeente kunt u de actuele stand zelf bekijken, of laat DEKO dit vrijblijvend
              voor u nakijken. De genoemde regelingen komen uit de officiële bijlage bij de provinciale regeling
              (Overheid.nl); de loketlinks komen van de officiële website van Provincie Fryslân.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-10 overflow-x-auto bg-white border border-[#E4E4E1] rounded-sm">
              <table className="w-full text-left text-sm" data-testid="gemeenten-tabel">
                <thead>
                  <tr className="border-b border-[#E4E4E1] text-xs uppercase tracking-widest text-[#222]/45">
                    <th className="px-5 py-4 font-medium">Gemeente</th>
                    <th className="px-5 py-4 font-medium">Regeling</th>
                    <th className="px-5 py-4 font-medium">Kozijnen/glas & bedrag</th>
                    <th className="px-5 py-4 font-medium whitespace-nowrap">Officiële bron</th>
                  </tr>
                </thead>
                <tbody>
                  {GEMEENTEN.map((g) => (
                    <tr key={g.naam} className="border-b border-[#F0F0EE] last:border-0 align-top" data-testid={`gemeente-rij-${g.naam.toLowerCase().replace(/[^a-z]/g, "")}`}>
                      <td className="px-5 py-4 text-[#2F3438] font-medium whitespace-nowrap">{g.naam}</td>
                      <td className="px-5 py-4 text-[#222]/65 font-light">{g.regeling}</td>
                      <td className="px-5 py-4 text-[#222]/65 font-light">{g.status}</td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <ExtLink href={g.bron} testid={`bron-${g.naam.toLowerCase().replace(/[^a-z]/g, "")}`}>Officiële regeling</ExtLink>
                        <div className="text-xs text-[#222]/40 mt-1">{g.bronLabel}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-xs text-[#222]/45 mt-4 max-w-3xl">
              Stand per {CHECK_DATE}. Of ramen, glas of kunststof kozijnen binnen een gemeentelijke regeling subsidiabel
              zijn, verschilt per regeling en wordt door DEKO per situatie gecontroleerd aan de hand van de officiële
              regeling van de betreffende gemeente.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SUBSIDIECHECK */}
      <section className="bg-[#F7F7F5] py-20 md:py-28" data-testid="subsidiecheck-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">DEKO Subsidiecheck</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#2F3438] mt-5 leading-tight">Benieuwd welke subsidie u mogelijk kunt krijgen?</h2>
            <p className="text-[#222]/60 font-light leading-relaxed mt-5 max-w-md">
              DEKO kijkt vrijblijvend met u mee. Vul uw gegevens in en wij zoeken uit welke landelijke, provinciale en
              gemeentelijke regelingen in uw situatie mogelijk van toepassing zijn — inclusief de actuele voorwaarden en
              budgetten.
            </p>
            <Link to="/offerte" data-testid="subsidie-offerte-cta" className="group inline-flex items-center gap-3 mt-8 border border-[#2F3438]/30 text-[#2F3438] px-6 py-3.5 rounded-sm hover:bg-[#2F3438] hover:text-white transition-colors duration-300">
              <span className="text-sm font-medium">Vraag vrijblijvend een offerte aan</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-7">
            <div className="bg-white border border-[#E4E4E1] rounded-sm p-8 sm:p-10">
              <SubsidieCheckForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRONNEN + DISCLAIMER */}
      <section className="bg-white py-20 md:py-24" data-testid="bronnen-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#2F3438]">Officiële informatiebronnen.</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#ECECEA] border border-[#ECECEA]">
              <div className="bg-white p-8">
                <div className="text-xs uppercase tracking-widest text-[#222]/40 mb-4">Landelijk</div>
                <div className="space-y-3 text-sm">
                  <div><ExtLink href="https://www.rvo.nl/subsidies-financiering/isde/woningeigenaren/isolatiemaatregelen" testid="bron-rvo-isde">RVO – ISDE isolatiemaatregelen</ExtLink></div>
                  <div><ExtLink href="https://www.rvo.nl/subsidies-financiering/isde/meldcodelijsten/hoogrendementsglas" testid="bron-rvo-meldcodes">RVO – meldcodes hoogrendementsglas</ExtLink></div>
                </div>
              </div>
              <div className="bg-white p-8">
                <div className="text-xs uppercase tracking-widest text-[#222]/40 mb-4">Provinciaal</div>
                <div className="space-y-3 text-sm">
                  <div><ExtLink href={FRIESE_PLUS.bron} testid="bron-fryslan">Provincie Fryslân – Friese Plus</ExtLink></div>
                  <div><ExtLink href={FRIESE_PLUS.regelgeving} testid="bron-cvdr">Officiële regeling (Overheid.nl)</ExtLink></div>
                </div>
              </div>
              <div className="bg-white p-8">
                <div className="text-xs uppercase tracking-widest text-[#222]/40 mb-4">Lokale regelgeving</div>
                <div className="space-y-3 text-sm">
                  <div><ExtLink href="https://lokaleregelgeving.overheid.nl/" testid="bron-overheid">Overheid.nl – lokale regelgeving</ExtLink></div>
                  <div className="text-[#222]/50 font-light text-xs leading-relaxed">Deze algemene bronnen vervangen niet de individuele bronverwijzing per gemeente in de tabel hierboven.</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="mt-10 border-t border-[#ECECEA] pt-8 text-sm text-[#222]/50 font-light leading-relaxed max-w-4xl" data-testid="subsidie-disclaimer">
              <p>
                Actueel gecontroleerd op {CHECK_DATE}. Subsidievoorwaarden, subsidieplafonds en beschikbare budgetten
                kunnen wijzigen of uitgeput raken. De informatie op deze pagina is bedoeld als algemene informatie en
                vormt geen garantie op subsidietoekenning. De officiële voorwaarden van Rijksdienst voor Ondernemend
                Nederland, Provincie Fryslân en de betreffende gemeente zijn altijd leidend. DEKO helpt u graag om
                mogelijke subsidieregelingen voor uw woning inzichtelijk te maken.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
